import { type PropsWithChildren, type ReactNode } from "react";

import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { LogOut } from "lucide-react";

import logoFull from "@/shared/assets/logo/logo-full.svg";

type Props = PropsWithChildren & {
  className?: string;
  childrenContainerClassName?: string;
  showLogoutButton?: boolean;
  logoClassName?: string;
  onLogout?: () => void;
  showTopBar?: boolean;
  topBarLeftContent?: ReactNode;
};

export default function FullscreenLayout({
  children,
  className = "lg:overflow-y-auto overflow-hidden",
  childrenContainerClassName = "md:justify-center px-4 sm:px-0 lg:px-0",
  showLogoutButton = false,
  logoClassName = "hidden lg:flex items-center fixed top-2 left-0 right-0 z-50 px-4 py-4",
  onLogout,
  showTopBar = false,
  topBarLeftContent,
}: Props) {
  return (
    <div
      className={cn(
        "bg-subtle flex flex-col w-full",
        showTopBar ? "h-screen" : "items-center min-h-[100dvh] overflow-y-auto h-full pt-4 sm:pt-0",
        className,
      )}
    >
      {showTopBar ? (
        <header className="shrink-0 w-full">
          <div className="flex items-center justify-between bg-white px-4 py-4 sm:px-6 sm:py-6">
            <div className="flex flex-1 min-h-9 items-center">{topBarLeftContent}</div>
            <img src={logoFull} alt="Elayne" className="h-7" />
            <div className="flex flex-1 min-h-9 items-center justify-end" />
          </div>
          <div className="h-1 w-full bg-stone-100" />
        </header>
      ) : (
        <div className="flex flex-col items-center justify-center z-10 md:py-4 sm:px-4 w-full flex-grow sm:h-full">
          <div className="flex w-full items-center justify-between flex-row gap-2 sm:gap-8 mx-auto px-4">
            <div className={logoClassName}>
              <img src={logoFull} alt="Elayne" className="absolute h-[28px] top-4 left-7" />
            </div>
            {showLogoutButton && onLogout && (
              <div>
                <Button onClick={onLogout} variant="outline" size="sm">
                  <LogOut className="w-4 h-4" />
                  <span className="ml-2">Logout</span>
                </Button>
              </div>
            )}
          </div>

          <div
            className={cn(
              "flex flex-col items-center sm:mt-32 md:mt-0 justify-start md:justify-center flex-grow w-full sm:h-full",
              childrenContainerClassName,
            )}
          >
            {children}
          </div>
        </div>
      )}

      {showTopBar && (
        <main className="flex-1 overflow-hidden bg-subtle">
          <div className="h-full overflow-y-auto flex flex-col">
            <div
              className={cn(
                "my-auto flex justify-center px-4 pb-16 sm:px-6 sm:pb-24",
                childrenContainerClassName,
              )}
            >
              {children}
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
